import React, { useState, useEffect, useRef } from "react";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import * as XLSX from "xlsx";
import { 
  InvoicedBySalesReport, 
  CustomerDetails1, 
  RequestInput, 
  CustomerSalesReportResponse1 
} from "../../services/edit-users/edit-users.service";

const InvoicedByReportTable: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [data, setData] = useState<CustomerDetails1[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const toast = useRef<Toast>(null);

  // FIXED: Preserve local date instead of converting to UTC
  const formatDate = (date: Date | null) =>
    date
      ? `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`
      : "";

  const fetchData = async (pageNumber: number = 1) => {
    if (!startDate || !endDate) return;

    setLoading(true);
    const filter: RequestInput = {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      pageNumber: pageNumber,
      pageSize: rows,
    };

    try {
      const response: CustomerSalesReportResponse1 | undefined = await InvoicedBySalesReport(filter);
      const reports = response?.salesReports ?? [];
      setData(reports);
      setTotalRecords(response?.totalRecords ?? 0);
    } catch (error) {
      console.error("Error fetching InvoicedBy data:", error);
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFirst(0);
    if (startDate && endDate) {
      fetchData(1);
    }
  }, [startDate, endDate, rows]);

  const onPageChange = (e: DataTableStateEvent) => {
    const newPage = Math.floor(e.first / e.rows) + 1;
    setFirst(e.first);
    setRows(e.rows);
    fetchData(newPage);
  };

  // Filter data based on search term
  const filteredData = data.filter(item => 
    item.invoicedBy?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Export function for Invoiced By Sales Report
  const exportInvoicedByReportExcel = (reportData: CustomerDetails1[]) => {
    const data = reportData.map((row) => ({
      "Invoiced By": row.invoicedBy || 'N/A',
      "Taxable Amount": row.taxableAmount?.toFixed(2) || '0.00',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Set column widths for better formatting
    const colWidths = [
      { wch: 30 }, // Invoiced By column
      { wch: 20 }, // Taxable Amount column
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, "Invoiced By Report");
    
    // Generate filename with current date
    const today = new Date();
    const dateString = `${today.getFullYear()}-${(today.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
    XLSX.writeFile(wb, `Invoiced_By_Report_${dateString}.xlsx`);
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select both start and end dates before exporting.',
        life: 3000
      });
      return;
    }

    if (!data || data.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No data to export.',
        life: 3000
      });
      return;
    }

    setExportLoading(true);
    try {
      // Export all data (not just current page)
      const filter: RequestInput = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        pageNumber: 1,
        pageSize: totalRecords,
      };

      const response: CustomerSalesReportResponse1 | undefined = await InvoicedBySalesReport(filter);
      const allReports = response?.salesReports ?? [];
      
      exportInvoicedByReportExcel(allReports);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Report exported successfully.',
        life: 3000
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export report. Please try again.',
        life: 3000
      });
    } finally {
      setExportLoading(false);
    }
  };

  const formatRupees = (amount?: number) =>
    amount !== undefined
      ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount)
      : "₹0.00";
    
  return (
    <div className="p-6">
      <Toast ref={toast} />
      <h2 className="text-2xl font-bold text-center mb-6 text-black">
        Invoiced By Sales Report
      </h2>

      {/* Date Filter, Search, and Export Button */}
      <div className="flex justify-between items-end mb-6">
        <div className="flex gap-6">
          <div>
            <label className="block mb-1 font-medium text-black">Start Date</label>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              placeholder="Select Start Date"
              className="border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium text-black">End Date</label>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value as Date)}
              dateFormat="dd/mm/yy"
              placeholder="Select End Date"
              className="border border-gray-300 rounded"
            />
          </div>
          <div>
            <label className="block mb-1 font-medium text-black">Search</label>
            <InputText
              placeholder="Search Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="p-inputtext-sm w-64 border border-gray-300 rounded"
            />
          </div>
        </div>
        <Button
          label="Export to Excel"
          icon="pi pi-download"
          className="p-button-outlined px-5 py-2 ml-auto"
          onClick={handleExport}
          loading={exportLoading}
          disabled={!startDate || !endDate || data.length === 0}
        />
      </div>

      {/* Data Table */}
      <div className="card p-4 shadow-md rounded-lg border border-gray-400 bg-white text-black">
        <DataTable
          value={filteredData}
          paginator
          rows={rows}
          first={first}
          totalRecords={filteredData.length}
          onPage={onPageChange}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          className="text-black border border-gray-300"
          emptyMessage="No records found. Please select date range and apply filter."
        >
          <Column field="invoicedBy" header="Invoiced By" sortable />
          <Column
            field="taxableAmount"
            header="Taxable Amount"
            sortable
            body={(row: CustomerDetails1) => formatRupees(row.taxableAmount)}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default InvoicedByReportTable;
